/**
 * Usage Tracker – Zählt verbrauchte Zeichen und berechnet Kosten.
 * Trackt Free-Tier-Kontingent mit Warnungen.
 *
 * Gezählt wird je Gerät, Monat und Stimmtyp. Jedes Gerät schreibt nur seinen
 * eigenen Zähler; so lassen sich die Zähler mehrerer Geräte beim
 * Synchronisieren ohne Konflikte zusammenführen. Angezeigt wird die Summe.
 * Google rechnet die Freikontingente ebenfalls je Stimmtyp ab.
 */

import { getFreeTierQuota, getPrice } from './settingsManager.js';
import { getDeviceId } from './deviceId.js';

const STORAGE_KEY = 'the-reader-usage';

/**
 * @typedef {{ [deviceId: string]: { [month: string]: { [tier: string]: number } } }} UsageCounters
 */

/** @type {UsageCounters} */
let counters = {};

/** Event listeners for usage updates */
const listeners = [];

/**
 * Gets the current billing month string
 * @returns {string} YYYY-MM
 */
function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Loads the counters from localStorage
 */
export function load() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    counters = stored?.counters ?? fromLegacyFormat(stored);
  } catch {
    counters = {};
  }
}

/**
 * Übernimmt den Zähler aus dem früheren Format (eine Zahl je Monat) für
 * dieses Gerät. Das alte Format kannte keine Trennung nach Stimmtyp; die
 * Zahl wird dem zuletzt genutzten Typ zugeschlagen.
 * @param {{charsUsed?: number, billingMonth?: string, tier?: string}|null} legacy
 * @returns {UsageCounters}
 */
function fromLegacyFormat(legacy) {
  if (!legacy?.charsUsed || !legacy.billingMonth) return {};
  const tier = legacy.tier || 'Standard';
  return { [getDeviceId()]: { [legacy.billingMonth]: { [tier]: legacy.charsUsed } } };
}

/**
 * Saves the counters to localStorage
 */
function save() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ counters }));
  } catch {
    // Ignore
  }
}

/**
 * Adds characters to this device's counter (only for actual API calls, not cache hits)
 * @param {number} charCount
 * @param {string} tier
 */
export function addChars(charCount, tier) {
  const month = getCurrentMonth();
  const device = (counters[getDeviceId()] ??= {});
  const monthCounters = (device[month] ??= {});
  monthCounters[tier] = (monthCounters[tier] ?? 0) + charCount;
  save();
  notifyListeners();
}

/**
 * Liefert die Zähler aller Geräte für den Abgleich
 * @returns {UsageCounters}
 */
export function getCounters() {
  return structuredClone(counters);
}

/**
 * Übernimmt abgeglichene Zähler. Zusammenführen statt Ersetzen: Zeichen, die
 * während des Abgleichs dazugekommen sind, gehen so nicht verloren.
 * @param {UsageCounters} next
 */
export function applyCounters(next) {
  counters = mergeCounters(counters, next);
  save();
  notifyListeners();
}

/**
 * Führt Zähler zusammen. Jedes Gerät schreibt nur seinen eigenen Zähler, also
 * ist das Maximum je Eintrag immer der richtige Stand.
 * @param {UsageCounters} a
 * @param {UsageCounters} b
 * @returns {UsageCounters}
 */
export function mergeCounters(a, b) {
  const merged = structuredClone(a);
  for (const [device, months] of Object.entries(b)) {
    for (const [month, tiers] of Object.entries(months)) {
      for (const [tier, chars] of Object.entries(tiers)) {
        const target = ((merged[device] ??= {})[month] ??= {});
        target[tier] = Math.max(target[tier] ?? 0, chars);
      }
    }
  }
  return merged;
}

/**
 * Sums the characters of all devices for one tier and month
 * @param {string} tier
 * @param {string} month - YYYY-MM
 * @returns {number}
 */
function sumChars(tier, month) {
  return Object.values(counters).reduce((sum, device) => sum + (device[month]?.[tier] ?? 0), 0);
}

/**
 * Gets usage statistics for the current month
 * @param {string} [tier='Standard']
 * @returns {Object}
 */
export function getStats(tier = 'Standard') {
  const billingMonth = getCurrentMonth();
  const freeQuota = getFreeTierQuota(tier);
  const used = sumChars(tier, billingMonth);
  const remaining = Math.max(0, freeQuota - used);
  const percentage = Math.min(100, (used / freeQuota) * 100);
  const billableChars = Math.max(0, used - freeQuota);
  const cost = (billableChars / 1_000_000) * getPrice(tier);

  return {
    charsUsed: used,
    freeQuota,
    remaining,
    percentage,
    billableChars,
    cost,
    tier,
    billingMonth,
    isOverFree: used > freeQuota,
    warning80: percentage >= 80 && percentage < 100,
    warning100: percentage >= 100,
  };
}

/**
 * Formats a character count for display
 * @param {number} count
 * @returns {string}
 */
export function formatCharCount(count) {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1)} Mio.`;
  }
  if (count >= 1_000) {
    return `${Math.floor(count / 1_000)}.${String(count % 1_000)
      .padStart(3, '0')
      .slice(0, 3)}`;
  }
  return count.toString();
}

/**
 * Formats cost for display
 * @param {number} cost
 * @returns {string}
 */
export function formatCost(cost) {
  return `$${cost.toFixed(2)}`;
}

/**
 * Registers a listener for usage updates
 * @param {Function} callback
 * @returns {Function} Unsubscribe function
 */
export function onUpdate(callback) {
  listeners.push(callback);
  return () => {
    const idx = listeners.indexOf(callback);
    if (idx >= 0) listeners.splice(idx, 1);
  };
}

/**
 * Notifies all listeners of usage changes
 */
function notifyListeners() {
  for (const listener of listeners) {
    listener();
  }
}

/**
 * Resets all counters (for testing)
 */
export function reset() {
  counters = {};
  save();
  notifyListeners();
}
